import { clearHighlight, setHighlight } from './menu-actions';
import { useMenuContext } from './menu-context';
import { useCallback } from 'react';

/** CSS selector for navigable menu items (excludes disabled items) */
const NAVIGABLE_ITEM_SELECTOR = [
    '[role="menuitem"]:not([aria-disabled="true"])',
    '[role="menuitemcheckbox"]:not([aria-disabled="true"])',
    '[role="menuitemradio"]:not([aria-disabled="true"])',
].join(', ');

/** Query all navigable (non-disabled) menu items from a container in DOM order */
export function getNavigableItems(container: HTMLElement | null): HTMLElement[] {
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>(NAVIGABLE_ITEM_SELECTOR));
}

/**
 * Hook that returns mouse interaction handlers for menu items.
 * Dispatches SET_HIGHLIGHT on mouse enter and CLEAR_HIGHLIGHT on mouse leave.
 */
export function useMenuItemInteraction(disabled?: boolean) {
    const { dispatch, contentRef } = useMenuContext();

    const onMouseEnter = useCallback(
        (e: React.MouseEvent<HTMLElement>) => {
            if (disabled) return;
            const items = getNavigableItems(contentRef.current);
            const index = items.indexOf(e.currentTarget as HTMLElement);
            if (index >= 0) {
                dispatch(setHighlight(index));
            }
        },
        [disabled, contentRef, dispatch],
    );

    const onMouseLeave = useCallback(() => {
        dispatch(clearHighlight());
    }, [dispatch]);

    return { onMouseEnter, onMouseLeave };
}
